import React, { useState, useImperativeHandle, forwardRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  IconButton,
  Paper,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    maxHeight: 600,
    overflow: 'auto'
  },
  table: {
    minWidth: 1200
  },
  cell: {
    padding: theme.spacing(1),
    minWidth: 100
  },
  smallCell: {
    padding: theme.spacing(0.5),
    minWidth: 60,
    maxWidth: 80
  },
  textField: {
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 1),
      fontSize: '0.875rem'
    }
  },
  select: {
    '& .MuiSelect-select': {
      padding: theme.spacing(0.5, 1),
      fontSize: '0.875rem'
    }
  },
  checkbox: {
    padding: theme.spacing(0.5)
  },
  addButton: {
    margin: theme.spacing(1, 0)
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    position: 'sticky',
    top: 0,
    zIndex: 1
  }
}))

const DATA_TYPES = [
  'int',
  'bigint',
  'decimal',
  'bool',
  'string',
  'text',
  'date',
  'datetime',
  'json',
  'binary',
  'unknown'
]

// TYPE_CLASSES no longer needed since typeClass is auto-derived from type

const DatapointsEditTable = forwardRef(({ initialDatapoints = [] }, ref) => {
  const classes = useStyles()
  const [datapoints, setDatapoints] = useState(() =>
    initialDatapoints.map((dp, index) => ({
      ...dp,
      tempId: dp.catalogDatapointId || `temp_${index}`,
      isNew: !dp.catalogDatapointId
    }))
  )

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    getData: () => datapoints.map(dp => {
      // Remove only internal tracking fields, preserve ALL original fields
      const { tempId, isNew, modified, ...data } = dp
      return data
    }),
    getChanges: () => ({
      added: datapoints.filter(dp => dp.isNew).map(dp => {
        // Remove only internal tracking fields, preserve ALL original fields
        const { tempId, isNew, modified, ...data } = dp
        return data
      }),
      modified: datapoints.filter(dp => !dp.isNew && dp.modified).map(dp => {
        // Remove only internal tracking fields, preserve ALL original fields
        const { tempId, isNew, modified, ...data } = dp
        return data
      }),
      deleted: [] // Would need to track deletions separately
    }),
    reset: () => setDatapoints(initialDatapoints.map((dp, index) => ({
      ...dp, // Preserve ALL original fields
      tempId: dp.catalogDatapointId || `temp_${index}`,
      isNew: !dp.catalogDatapointId
    }))),
    toggleAllAiEnabled: () => {
      const allEnabled = datapoints.every(dp => dp.isAiEnabled === true)
      const newValue = !allEnabled
      setDatapoints(prev => prev.map(dp => ({
        ...dp,
        isAiEnabled: newValue,
        modified: !dp.isNew || dp.modified
      })))
      return newValue // Return the new state for button label
    }
  }))

  const updateDatapoint = useCallback((tempId, field, value) => {
    setDatapoints(prev => prev.map(dp =>
      dp.tempId === tempId
        ? { ...dp, [field]: value, modified: !dp.isNew || dp.modified }
        : dp
    ))
  }, [])

  const deleteDatapoint = useCallback((tempId) => {
    setDatapoints(prev => prev.filter(dp => dp.tempId !== tempId))
  }, [])

  const addDatapoint = useCallback(() => {
    // Create a new datapoint with sensible defaults, ensuring all expected fields are present
    const newDatapoint = {
      // Internal tracking fields
      tempId: `temp_${Date.now()}`,
      isNew: true,
      modified: false,

      // Core datapoint fields with defaults
      catalogDatapointId: null, // Will be assigned by backend
      accessor: '',
      displayName: '',
      type: 'string',
      typeClass: 'text', // Will be auto-corrected by schema
      enumerationId: null,
      ordinal: Math.max(...datapoints.map(dp => dp.ordinal || 0), 0) + 1,
      nullable: true,
      length: null,
      numericScale: null,
      numericPrecision: null,
      description: null,

      // Additional fields that might exist in the schema
      pii: false,
      ai: false,
      deprecated: false,
      hidden: false,

      // Add any additional fields from the first existing datapoint (if any) to maintain structure
      ...(datapoints.length > 0
        ? Object.keys(datapoints[0])
          .filter(key => !['tempId', 'isNew', 'modified'].includes(key))
          .reduce((acc, key) => {
            if (!(key in newDatapoint)) {
              acc[key] = null // Default any unknown fields to null
            }
            return acc
          }, {})
        : {}
      )
    }
    setDatapoints(prev => [...prev, newDatapoint])
  }, [datapoints])

  const handleTextFieldChange = useCallback((tempId, field) => (event) => {
    const value = event.target.value
    updateDatapoint(tempId, field, value === '' ? null : value)
  }, [updateDatapoint])

  const handleSelectChange = useCallback((tempId, field) => (event) => {
    updateDatapoint(tempId, field, event.target.value)
  }, [updateDatapoint])

  const handleCheckboxChange = useCallback((tempId, field) => (event) => {
    updateDatapoint(tempId, field, event.target.checked)
  }, [updateDatapoint])

  const handleNumberChange = useCallback((tempId, field) => (event) => {
    const value = event.target.value
    const numValue = value === '' ? null : parseInt(value, 10)
    updateDatapoint(tempId, field, isNaN(numValue) ? null : numValue)
  }, [updateDatapoint])

  return (
    <div>
      <div className={classes.addButton}>
        <IconButton
          color='primary'
          onClick={addDatapoint}
          size='small'
        >
          <AddIcon />
        </IconButton>
        <Typography variant='body2' component='span' style={{ marginLeft: 8 }}>
          Add Datapoint
        </Typography>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Actions</TableCell>
              <TableCell className={classes.headerCell}>Accessor</TableCell>
              <TableCell className={classes.headerCell}>Display Name</TableCell>
              <TableCell className={classes.headerCell}>Type Class</TableCell>
              <TableCell className={classes.headerCell}>Type</TableCell>
              <TableCell className={classes.headerCell}>Enumeration</TableCell>
              <TableCell className={classes.headerCell}>Ordinal</TableCell>
              <TableCell className={classes.headerCell}>Nullable</TableCell>
              <TableCell className={classes.headerCell}>Length</TableCell>
              <TableCell className={classes.headerCell}>Scale</TableCell>
              <TableCell className={classes.headerCell}>Precision</TableCell>
              <TableCell className={classes.headerCell}>PII</TableCell>
              <TableCell className={classes.headerCell}>AI</TableCell>
              <TableCell className={classes.headerCell}>Deprecated</TableCell>
              <TableCell className={classes.headerCell}>Hidden</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datapoints.map((datapoint) => (
              <TableRow key={datapoint.tempId}>
                <TableCell className={classes.smallCell}>
                  <IconButton
                    size='small'
                    onClick={() => deleteDatapoint(datapoint.tempId)}
                    color='secondary'
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </TableCell>

                <TableCell className={classes.cell}>
                  <TextField
                    value={datapoint.accessor || ''}
                    onChange={handleTextFieldChange(datapoint.tempId, 'accessor')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    className={classes.textField}
                    placeholder='field_name'
                  />
                </TableCell>

                <TableCell className={classes.cell}>
                  <TextField
                    value={datapoint.displayName || ''}
                    onChange={handleTextFieldChange(datapoint.tempId, 'displayName')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    className={classes.textField}
                    placeholder='Display Name'
                  />
                </TableCell>

                <TableCell className={classes.cell}>
                  <TextField
                    value={datapoint.typeClass || ''}
                    variant='outlined'
                    size='small'
                    fullWidth
                    className={classes.textField}
                    disabled
                  />
                </TableCell>

                <TableCell className={classes.cell}>
                  <FormControl variant='outlined' size='small' fullWidth>
                    <Select
                      value={datapoint.type || 'string'}
                      onChange={handleSelectChange(datapoint.tempId, 'type')}
                      className={classes.select}
                    >
                      {DATA_TYPES.map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <TextField
                    value={datapoint.enumerationId || ''}
                    onChange={handleNumberChange(datapoint.tempId, 'enumerationId')}
                    variant='outlined'
                    size='small'
                    type='number'
                    className={classes.textField}
                    placeholder='ID'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <TextField
                    value={datapoint.ordinal || ''}
                    onChange={handleNumberChange(datapoint.tempId, 'ordinal')}
                    variant='outlined'
                    size='small'
                    type='number'
                    className={classes.textField}
                    required
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <Checkbox
                    checked={!!datapoint.nullable}
                    onChange={handleCheckboxChange(datapoint.tempId, 'nullable')}
                    className={classes.checkbox}
                    color='primary'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <TextField
                    value={datapoint.length || ''}
                    onChange={handleNumberChange(datapoint.tempId, 'length')}
                    variant='outlined'
                    size='small'
                    type='number'
                    className={classes.textField}
                    placeholder='Len'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <TextField
                    value={datapoint.numericScale || ''}
                    onChange={handleNumberChange(datapoint.tempId, 'numericScale')}
                    variant='outlined'
                    size='small'
                    type='number'
                    className={classes.textField}
                    placeholder='Scale'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <TextField
                    value={datapoint.numericPrecision || ''}
                    onChange={handleNumberChange(datapoint.tempId, 'numericPrecision')}
                    variant='outlined'
                    size='small'
                    type='number'
                    className={classes.textField}
                    placeholder='Prec'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <Checkbox
                    checked={!!datapoint.isPii}
                    onChange={handleCheckboxChange(datapoint.tempId, 'isPii')}
                    className={classes.checkbox}
                    color='primary'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <Checkbox
                    checked={!!datapoint.isAiEnabled}
                    onChange={handleCheckboxChange(datapoint.tempId, 'isAiEnabled')}
                    className={classes.checkbox}
                    color='primary'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <Checkbox
                    checked={!!datapoint.isDeprecated}
                    onChange={handleCheckboxChange(datapoint.tempId, 'isDeprecated')}
                    className={classes.checkbox}
                    color='primary'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <Checkbox
                    checked={!!datapoint.isHidden}
                    onChange={handleCheckboxChange(datapoint.tempId, 'isHidden')}
                    className={classes.checkbox}
                    color='primary'
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
})

DatapointsEditTable.propTypes = {
  initialDatapoints: PropTypes.arrayOf(PropTypes.shape({
    // Core datapoint fields
    catalogDatapointId: PropTypes.number, // Preserved in output
    accessor: PropTypes.string,
    displayName: PropTypes.string,
    type: PropTypes.string,
    typeClass: PropTypes.string,
    enumerationId: PropTypes.number,
    ordinal: PropTypes.number,
    nullable: PropTypes.bool,
    length: PropTypes.number,
    numericScale: PropTypes.number,
    numericPrecision: PropTypes.number,
    description: PropTypes.string,

    // Additional fields that may be present
    isPii: PropTypes.bool,
    isAiEnabled: PropTypes.bool,
    isDeprecated: PropTypes.bool,
    isHidden: PropTypes.bool

    // Allow for additional unknown fields to be preserved
    // Note: PropTypes doesn't have a great way to express "any additional fields"
    // but the component will preserve them regardless
  }))
}

DatapointsEditTable.displayName = 'DatapointsEditTable'

export default DatapointsEditTable
